/**
 * Responsibility: sort card names the two ways people actually want them.
 *
 * A card name carries two orderings and they disagree. "Charizard #4" sorts
 * under C alphabetically and at position 4 in its set, and which one is
 * wanted depends entirely on the question: *find the Charizard* wants the
 * alphabet, *is my set complete* wants the numbers.
 *
 * Numbering is not plain integers either. Sets run 1-150 and then H1-H32, or
 * carry a letter on a variant - 143A is the same card as 143 - and a naive
 * numeric parse puts H1 either first or nowhere. So the key is built in two
 * parts: the numeric run, then whatever was attached to it, which keeps 143
 * before 143A and every H-prefixed card together after the plain ones.
 */

/** Which of the two orderings a name column is using. */
export type NameSort = 'alphabetical' | 'number';

/** `#123`, `#H12`, `#143A` - the collector number as printed. */
const NUMBER = /#\s*([A-Za-z]*)(\d+)([A-Za-z]*)/;

/** Sorts after every real number, so unnumbered cards land at the end. */
const UNNUMBERED = Number.MAX_SAFE_INTEGER;

/** Wide enough for any real set, so the key sorts as text without padding maths. */
const DIGITS = 6;

/**
 * A sortable key for a name, in the chosen ordering.
 *
 * Returns a string in both modes rather than a number in one: the table
 * compares whatever it is given, and one column that sometimes yields a
 * number and sometimes a string is a column that sorts differently
 * depending on which rows happen to be loaded.
 */
export function nameSortValue(name: string, mode: NameSort): string {
  if (mode === 'alphabetical') {
    // Without the number, so "Charizard #4" and "Charizard #102" sit
    // together under C rather than being separated by their digits.
    return name.replace(NUMBER, '').trim().toLocaleLowerCase();
  }

  const found = NUMBER.exec(name);
  if (!found) return String(UNNUMBERED);

  const [, prefix, digits, suffix] = found;
  // Prefix first so H1-H32 group after the plain run rather than being
  // interleaved with it; then the number, padded so 9 precedes 10; then the
  // suffix so 143 precedes 143A.
  return [
    prefix.toLocaleLowerCase(),
    digits.padStart(DIGITS, '0'),
    suffix.toLocaleLowerCase(),
  ].join('|');
}
